import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Autocomplete,
  Grid,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import Page from '../../../components/Page';
import Loader from '../../../components/Loader';
import {
  getAgentUsageHistory,
  AgentUsageHistoryItem,
  AgentUsageHistoryResponse,
} from '../../../services/company-credits';
import {
  getCompanyCreditUsageHistory,
  CompanyCreditUsageHistoryItem,
  CompanyCreditUsageHistoryResponse,
} from '../../../services/company-credit-usage-log';
import { toastHandler } from '../../../utils/toastHandler';
import moment from 'moment';
import { useAppSelector } from '../../../store';
import { getCompaniesList } from '../../../services/companies-list';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`credit-usage-tabpanel-${index}`}
      aria-labelledby={`credit-usage-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const CreditUsageHistory = () => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Agent, 1 = Company
  
  // Agent usage state
  const [agentLoading, setAgentLoading] = useState(true);
  const [agentData, setAgentData] = useState<AgentUsageHistoryItem[]>([]);
  const [agentPage, setAgentPage] = useState(0);
  const [agentLimit, setAgentLimit] = useState(20);
  const [agentTotal, setAgentTotal] = useState(0);
  const [agentSummary, setAgentSummary] = useState<{
    totalCreditsUsed: number;
    byAgent: Array<{
      agentId: string;
      agentName: string;
      creditsUsed: number;
    }>;
  } | null>(null);

  // Company usage state
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companyData, setCompanyData] = useState<CompanyCreditUsageHistoryItem[]>([]);
  const [companyPage, setCompanyPage] = useState(0);
  const [companyLimit, setCompanyLimit] = useState(20);
  const [companyTotal, setCompanyTotal] = useState(0);
  const [companySummary, setCompanySummary] = useState<{
    totalCreditsUsed: number;
  } | null>(null);

  // Common state
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const isAdmin = userRole === 'admin' || userRole === 'adminTier2' || userRole === 'adminTier3';

  // Fetch companies list for admin
  useEffect(() => {
    if (isAdmin) {
      const fetchCompanies = async () => {
        try {
          setLoadingCompanies(true);
          const response = await getCompaniesList({ limit: 1000 });
          setCompanies(response.data || []);
        } catch (error: any) {
          console.error('Error fetching companies:', error);
          // Only show toast if it's a real error, not just empty response
          if (error?.message) {
            toastHandler(error);
          }
        } finally {
          setLoadingCompanies(false);
        }
      };
      fetchCompanies();
    }
  }, [isAdmin]);

  // Fetch agent usage history
  const fetchAgentData = async () => {
    try {
      setAgentLoading(true);
      const response: AgentUsageHistoryResponse = await getAgentUsageHistory({
        page: agentPage + 1,
        limit: agentLimit,
        companyId: selectedCompany || undefined,
      });
      setAgentData(response.data);
      setAgentTotal(response.total);
      setAgentSummary(response.summary);
    } catch (error: any) {
      console.error('Error fetching agent usage history:', error);
      toastHandler(error);
    } finally {
      setAgentLoading(false);
    }
  };

  // Fetch company usage history
  const fetchCompanyData = async () => {
    try {
      setCompanyLoading(true);
      const response: CompanyCreditUsageHistoryResponse = await getCompanyCreditUsageHistory({
        page: companyPage + 1,
        limit: companyLimit,
        companyId: selectedCompany || undefined,
      });
      setCompanyData(response.data);
      setCompanyTotal(response.total);
      setCompanySummary(response.summary);
    } catch (error: any) {
      console.error('Error fetching company usage history:', error);
      toastHandler(error);
    } finally {
      setCompanyLoading(false);
    }
  };

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 0) {
      fetchAgentData();
    } else {
      fetchCompanyData();
    }
  }, [activeTab, agentPage, agentLimit, companyPage, companyLimit, selectedCompany]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setAgentPage(0);
    setCompanyPage(0);
  };

  const handleAgentPageChange = (event: unknown, newPage: number) => {
    setAgentPage(newPage);
  };

  const handleAgentRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgentLimit(parseInt(event.target.value, 10));
    setAgentPage(0);
  };

  const handleCompanyPageChange = (event: unknown, newPage: number) => {
    setCompanyPage(newPage);
  };

  const handleCompanyRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyLimit(parseInt(event.target.value, 10));
    setCompanyPage(0);
  };

  if ((activeTab === 0 && agentLoading && agentData.length === 0) || 
      (activeTab === 1 && companyLoading && companyData.length === 0)) {
    return <Loader />;
  }

  return (
    <Page title="Credit Usage History">
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Credit Usage History
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="credit usage history tabs">
            <Tab label="Agent Usage" />
            <Tab label="Company Usage" />
          </Tabs>
        </Box>

        {/* Company Filter for Admin */}
        {isAdmin && (
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={companies.find((c) => c.id === selectedCompany) || null}
              onChange={(event, newValue) => {
                setSelectedCompany(newValue?.id || null);
                setAgentPage(0);
                setCompanyPage(0);
              }}
              loading={loadingCompanies}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Company"
                  placeholder="Select a company or leave empty to see all"
                  variant="outlined"
                  fullWidth
                />
              )}
              sx={{ maxWidth: 400 }}
            />
          </Box>
        )}

        {/* Agent Usage Tab */}
        <TabPanel value={activeTab} index={0}>
          {/* Summary Cards */}
          {agentSummary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Total Credits Used
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {agentSummary.totalCreditsUsed}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Agents Count
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {agentSummary.byAgent.length}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Card>
            <CardContent>
              <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      {isAdmin && <TableCell>Company</TableCell>}
                      <TableCell>Agent Name</TableCell>
                      <TableCell>Agent Email</TableCell>
                      <TableCell>Advertise Title</TableCell>
                      <TableCell>Advertise Number</TableCell>
                      <TableCell>Credits Used</TableCell>
                      <TableCell>Used At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agentData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 7 : 6} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No agent usage history found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      agentData.map((item) => (
                        <TableRow key={item.id}>
                          {isAdmin && (
                            <TableCell>
                              {item.companyName || 'N/A'}
                              {item.companyEmail && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {item.companyEmail}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell>{item.agentName}</TableCell>
                          <TableCell>{item.agentEmail}</TableCell>
                          <TableCell>{item.advertiseTitle}</TableCell>
                          <TableCell>{item.advertiseNumber}</TableCell>
                          <TableCell>{item.creditsUsed}</TableCell>
                          <TableCell>
                            {moment(item.usedAt).format('YYYY-MM-DD HH:mm')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={agentTotal}
                page={agentPage}
                onPageChange={handleAgentPageChange}
                rowsPerPage={agentLimit}
                onRowsPerPageChange={handleAgentRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* Company Usage Tab */}
        <TabPanel value={activeTab} index={1}>
          {/* Summary Cards */}
          {companySummary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Total Credits Used
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {companySummary.totalCreditsUsed}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Card>
            <CardContent>
              <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      {isAdmin && <TableCell>Company</TableCell>}
                      <TableCell>Advertise Title</TableCell>
                      <TableCell>Advertise Number</TableCell>
                      <TableCell>Credits Used</TableCell>
                      <TableCell>Used At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companyData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 5 : 4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No company usage history found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      companyData.map((item) => (
                        <TableRow key={item.id}>
                          {isAdmin && (
                            <TableCell>
                              {item.companyName || 'N/A'}
                              {item.companyEmail && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {item.companyEmail}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell>{item.advertiseTitle}</TableCell>
                          <TableCell>{item.advertiseNumber}</TableCell>
                          <TableCell>{item.creditsUsed}</TableCell>
                          <TableCell>
                            {moment(item.usedAt).format('YYYY-MM-DD HH:mm')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={companyTotal}
                page={companyPage}
                onPageChange={handleCompanyPageChange}
                rowsPerPage={companyLimit}
                onRowsPerPageChange={handleCompanyRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </CardContent>
          </Card>
        </TabPanel>
      </Container>
    </Page>
  );
};

export default CreditUsageHistory;

